import { NgPipesModule, ShufflePipe } from 'ngx-pipes';

import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { Location, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef, Component, DestroyRef, inject, signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { MiniBox } from '@app/components/mini-box/mini-box.component';
import { SerbianMapComponent } from '@app/components/serbian-map/serbian-map.component';
import { StatsCounterComponent } from '@app/components/stats-counter/stats-counter.component';
import { BasicSheetComponent } from '@app/dialogs/basic-sheet/basic-sheet.component';
import { RemoveHtmlTagsPipe } from '@app/pipes/remove-html-tags.pipe';
import { TranslitPipe } from '@app/pipes/translit.pipe';
import { TrimWordPipe } from '@app/pipes/trim-word.pipe';
import { CategoriesService, Category } from '@app/services/categories.service';
import {
  Chronology, chronologyBoxes, ChronologyService, ChronologyType
} from '@app/services/chronology.service';
import { heritageBoxes } from '@app/services/church.service';
import { HistoricalEvent, HistoricalEventsService } from '@app/services/historical-events.service';
import { instragramTypes } from '@app/services/instagram.service';
import { Quote, QuotesService } from '@app/services/quotes.service';
import { Song, SongsService } from '@app/services/songs.service';
import { Tag, TagGroup, TagsService } from '@app/services/tags.service';
import { Person, PersonsService } from '@services/persons.service';
import { SaintsService } from '@services/saints.service';
import { ThemeService } from '@services/theme.service';
import { UtilsService } from '@services/utils.service';

@Component({
  selector: 'app-welcome-page',
  imports: [
    NgTemplateOutlet,
    MatButtonModule,
    MatMenuModule,
    NgPipesModule,
    MatIconModule,
    SerbianMapComponent,
    StatsCounterComponent,
    TranslitPipe,
    TrimWordPipe,
    RemoveHtmlTagsPipe,
  ],
  templateUrl: './welcome-page.component.html',
  styleUrl: './welcome-page.component.scss',
  providers: [ShufflePipe]
})
export class WelcomePageComponent {
  private personsService = inject(PersonsService);
  private saintsService = inject(SaintsService);
  private chronologyService = inject(ChronologyService);
  private tagsService = inject(TagsService);
  private categoriesService = inject(CategoriesService);
  private historicalEventsService = inject(HistoricalEventsService);
  private quotesService = inject(QuotesService);
  private songsService = inject(SongsService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private location = inject(Location);
  private matBottomSheet = inject(MatBottomSheet);

  private router = inject(Router);
  private breakpointObserver = inject(BreakpointObserver);
  private shuffle = inject(ShufflePipe);

  public themeService = inject(ThemeService);
  public tileList: Person[] = [];
  public categoryList: Category[] = [];
  public currentDate!: string;
  public dailyQuotes: Quote[] = [];
  public songList: Song[] = [];
  public randomSong: Song | null = null;
  public randomEvent!: any;
  public randomQuote!: Quote;
  public randomBirthday!: Person;
  public dailyEvents: any[] = [];
  public timelineEvents: Chronology[] = [];
  public heritageBoxes = heritageBoxes;
  public tagBoxes: MiniBox[] = [];
  public cityTagBoxes: MiniBox[] = [];
  public funfactBoxes: MiniBox[] = [];
  public randomFunfact: MiniBox | null = null;
  public chronologyBoxes = chronologyBoxes;
  public cards: Person[] = [];
  public gamePoints!: any[];
  public totalScore!: number;
  public smallScreen = true;
  public biggerMap = false;
  public eventsSubtitle = 'Догађаји и празници';
  public eventsTitle = 'На данашњи дан';
  public showItem: boolean[] = [];
  public currentYear = new Date().getFullYear();
  public instragramTypes: any[] = instragramTypes;
  public surnameList: any[] = [];
  public firstnameList: any[] = [];
  public firstnameFemaleList: any[] = [];
  public firstnameMaleList: any[] = [];
  public greatestIndex = 0;
  public randomTimelineEvent: Chronology | null = null;

  // ── List pagination ────────────────────────────────────────────
  readonly ITEMS_PER_PAGE = 7;
  readonly sideCardCount = signal(3);
  readonly surnamePageIndex = signal(0);
  readonly femaleNamePageIndex = signal(0);
  readonly maleNamePageIndex = signal(0);
  readonly categoryPageIndex = signal(0);

  get surnamePageItems() {
    const s = this.surnamePageIndex() * this.ITEMS_PER_PAGE;
    return this.surnameList.slice(s, s + this.ITEMS_PER_PAGE);
  }
  get femaleNamePageItems() {
    const s = this.femaleNamePageIndex() * this.ITEMS_PER_PAGE;
    return this.firstnameFemaleList.slice(s, s + this.ITEMS_PER_PAGE);
  }
  get maleNamePageItems() {
    const s = this.maleNamePageIndex() * this.ITEMS_PER_PAGE;
    return this.firstnameMaleList.slice(s, s + this.ITEMS_PER_PAGE);
  }
  get categoryPageItems() {
    const s = this.categoryPageIndex() * this.ITEMS_PER_PAGE;
    return this.categoryList.slice(s, s + this.ITEMS_PER_PAGE);
  }

  get surnameTotalPages() { return Math.ceil(this.surnameList.length / this.ITEMS_PER_PAGE); }
  get femaleNameTotalPages() { return Math.ceil(this.firstnameFemaleList.length / this.ITEMS_PER_PAGE); }
  get maleNameTotalPages() { return Math.ceil(this.firstnameMaleList.length / this.ITEMS_PER_PAGE); }
  get categoryTotalPages() { return Math.ceil(this.categoryList.length / this.ITEMS_PER_PAGE); }

  prevSurnamePage(): void { this.surnamePageIndex.update(i => Math.max(0, i - 1)); }
  nextSurnamePage(): void { this.surnamePageIndex.update(i => Math.min(i + 1, this.surnameTotalPages - 1)); }
  prevFemaleNamePage(): void { this.femaleNamePageIndex.update(i => Math.max(0, i - 1)); }
  nextFemaleNamePage(): void { this.femaleNamePageIndex.update(i => Math.min(i + 1, this.femaleNameTotalPages - 1)); }
  prevMaleNamePage(): void { this.maleNamePageIndex.update(i => Math.max(0, i - 1)); }
  nextMaleNamePage(): void { this.maleNamePageIndex.update(i => Math.min(i + 1, this.maleNameTotalPages - 1)); }
  prevCategoryPage(): void { this.categoryPageIndex.update(i => Math.max(0, i - 1)); }
  nextCategoryPage(): void { this.categoryPageIndex.update(i => Math.min(i + 1, this.categoryTotalPages - 1)); }

  public get imagePrefix(): string {
    return this.themeService.isDarkMode()
      ? `radial-gradient(circle, rgb(0 0 0 / 85%) 0%, black 95%), linear-gradient(#353535, #222222), `
      : `radial-gradient(circle, rgb(255 255 255 / 80%) 0%, white 95%), linear-gradient(#c8c8c8, #c8c8c8), `;
  };

  async ngOnInit() {
    UtilsService.scrollTop();

    this.listenBreakpoints();
    this.checkDailyQuiz();

    await Promise.all([
      // getDailyHolidays and getDailyEvents share state, keep them sequential
      (async () => {
        await this.getDailyHolidays();
        await this.getDailyEvents();
      })(),
      this.getTiles(),
      this.getQuotes(),
      this.getTags(),
      this.getTimelineEvents(),
      this.getCategories(),
      this.getPersonCards(),
      this.getFunFacts(),
      this.getSongs(),
      this.getFirstnames(),
      this.getSurnames(),
      this.buildPersonTitleMap(),
    ]);

    this.startInterval();
  }

  startInterval() {
    this.eventIntervalId = setInterval(() => this.randomizeEvent(), 2500);
    this.birthdayIntervalId = setInterval(() => this.rotateBirthday(), 3000);
    this.quoteIntervalId = setInterval(() => this.rotateQuote(), 3000);
    this.heritageIntervalId = setInterval(() => this.rotateHeritage(), 3500);
    this.chronologyIntervalId = setInterval(() => this.rotateChronology(), 4000);
    this.timelineIntervalId = setInterval(() => this.rotateTimeline(), 5000);
    this.funfactIntervalId = setInterval(() => this.rotateFunfact(), 4000);
    this.instagramIntervalId = setInterval(() => this.rotateInstagram(), 4500);
    this.songIntervalId = setInterval(() => this.rotateSong(), 5000);
    this.tagIntervalId = setInterval(() => this.rotateTag(), 3800);
    this.cityIntervalId = setInterval(() => this.rotateCity(), 4200);
    this.greatestIntervalId = setInterval(() => this.rotateGreatest(), 5500);

    this.destroyRef.onDestroy(() => {
      clearInterval(this.eventIntervalId);
      clearInterval(this.birthdayIntervalId);
      clearInterval(this.quoteIntervalId);
      clearInterval(this.heritageIntervalId);
      clearInterval(this.chronologyIntervalId);
      clearInterval(this.timelineIntervalId);
      clearInterval(this.funfactIntervalId);
      clearInterval(this.instagramIntervalId);
      clearInterval(this.songIntervalId);
      clearInterval(this.tagIntervalId);
      clearInterval(this.cityIntervalId);
      clearInterval(this.greatestIntervalId);
    });
  }

  async getTags(): Promise<void> {
    const tags = await this.tagsService.getItems();

    const trendingTags = tags.filter(t => t.trending);
    UtilsService.shuffleListOrder(trendingTags);

    this.tagBoxes = trendingTags.map((tag: Tag) => ({
      style: '',
      imagePath: tag.imagePath || '',
      title: tag.name || '',
      link: tag.slug || '',
      icon: null,
      background: '',
    }));

    this.cityTagBoxes = tags
      .filter(t => t.group === TagGroup.cities)
      .map((tag: Tag) => ({
        style: '',
        imagePath: tag.imagePath || '',
        title: tag.name || '',
        link: tag.slug || '',
        icon: null,
        background: '',
      }));

    this.updateTagSlots();
    this.updateCitySlots();
  }


  listenBreakpoints() {
    this.biggerMap = this.breakpointObserver.isMatched([
      Breakpoints.Large,
      Breakpoints.XLarge,
    ]);
    this.smallScreen = this.breakpointObserver.isMatched([
      Breakpoints.Small,
      Breakpoints.HandsetPortrait
    ]);

    const sideBreakpoints = [
      '(max-width: 500px)',
      '(max-width: 700px)',
      '(max-width: 900px)',
      '(max-width: 1100px)',
      '(max-width: 1350px)',
      '(max-width: 1600px)',
      '(max-width: 1900px)',
      '(max-width: 2200px)',
      '(max-width: 2600px)',
    ];

    this.breakpointObserver.observe([
      Breakpoints.Web,
      Breakpoints.Small,
      Breakpoints.Medium,
      Breakpoints.Large,
      Breakpoints.XLarge,
      Breakpoints.HandsetPortrait,
      Breakpoints.HandsetLandscape,
      ...sideBreakpoints,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result.matches) {
          this.smallScreen = result.breakpoints[Breakpoints.Small]
            || result.breakpoints[Breakpoints.HandsetPortrait]
            || result.breakpoints[Breakpoints.HandsetLandscape];

          this.biggerMap = result.breakpoints[Breakpoints.Large]
            || result.breakpoints[Breakpoints.XLarge];
        }

        const bp = result.breakpoints;
        const count =
          bp['(max-width: 500px)']  ? 1 :
          bp['(max-width: 700px)']  ? 2 :
          bp['(max-width: 900px)']  ? 3 :
          bp['(max-width: 1100px)'] ? 4 :
          bp['(max-width: 1350px)'] ? 5 :
          bp['(max-width: 1600px)'] ? 6 :
          bp['(max-width: 1900px)'] ? 7 :
          bp['(max-width: 2200px)'] ? 8 :
          bp['(max-width: 2600px)'] ? 9 : 10;
        this.sideCardCount.set(count);
      });
  }

  async getSurnames(): Promise<void> {
    const persons = await this.personsService.getItems();

    this.surnameList = UtilsService.groupAndSortBy(persons, 'lastName')
      .slice(0, 70);
    this.surnameList.forEach(item => {
      item.slug = UtilsService.slugify(item.title);
    });

    this.cdr.detectChanges();
  }

  async getFirstnames(): Promise<void> {
    const persons = await this.personsService.getItems();

    this.firstnameList = UtilsService.groupAndSortBy(persons, 'firstName')
      .slice(0, 70);
    this.firstnameList.forEach(item => {
      item.slug = UtilsService.slugify(item.title);
    });

    this.firstnameFemaleList = UtilsService.groupAndSortByWhere(persons, 'firstName', p => p.gender === true)
      .slice(0, 70);
    this.firstnameFemaleList.forEach(item => {
      item.slug = UtilsService.slugify(item.title);
    });

    this.firstnameMaleList = UtilsService.groupAndSortByWhere(persons, 'firstName', p => p.gender !== true)
      .slice(0, 70);
    this.firstnameMaleList.forEach(item => {
      item.slug = UtilsService.slugify(item.title);
    });

    this.cdr.detectChanges();
  }


  async getQuotes(): Promise<void> {
    let response = await this.quotesService.getItemsQuery('important', '==', true);
    response = this.shuffle.transform(response);
    this.dailyQuotes = response.splice(0, 50);
    this.randomizeQuote();
  }

  async getSongs(): Promise<void> {
    let response = await this.songsService.getItemsQuery('important', '==', true);

    response = this.shuffle.transform(response);
    this.songList = response.splice(0, 50);
    this.randomSong = this.songList[0] ?? null;
  }


  randomizeQuote() {
    const randomIndex = Math.floor(Math.random() * this.dailyQuotes.length);
    this.randomQuote = this.dailyQuotes[randomIndex];
  }

  rotateQuote() {
    if (this.dailyQuotes.length < 2) return;

    let idx: number;
    do {
      idx = Math.floor(Math.random() * this.dailyQuotes.length);
    } while (this.dailyQuotes[idx] === this.randomQuote);

    this.randomQuote = this.dailyQuotes[idx];
    this.cdr.detectChanges();
  }

  randomizeEvent() {
    if (!this.dailyEvents.length) return;

    if (this.dailyEvents.length === 1) {
      this.randomEvent = this.dailyEvents[0];
    } else {
      const id = this.randomEvent?.id;
      const currentIndex = this.dailyEvents.findIndex(e => e.id === id);
      const max = this.dailyEvents.length - 1;
      const nextIndex = (currentIndex === max) ? 0 : (currentIndex + 1);
      this.randomEvent = this.dailyEvents[nextIndex];
    }

    if (this.randomEvent) {
      this.randomEvent.fullDate = this.randomEvent.saintDate
        ? `${this.randomEvent.saintDay}.${this.randomEvent.saintMonth}.`
        : `${this.randomEvent.day}.${this.randomEvent.month}.`;

      const match = this.randomEvent.description?.match(/^(\d{4})/);
      const year = match ? match[1] : null;

      if (year) {
        this.randomEvent.fullDate += `${year}.`;
      }
    }

    this.cdr.detectChanges();
  }

  private setEventFullDate(): void {
    if (!this.randomEvent) return;
    this.randomEvent.fullDate = this.randomEvent.saintDate
      ? `${this.randomEvent.saintDay}.${this.randomEvent.saintMonth}.`
      : `${this.randomEvent.day}.${this.randomEvent.month}.`;
    const match = this.randomEvent.description?.match(/^(\d{4})/);
    const year = match ? match[1] : null;
    if (year) this.randomEvent.fullDate += `${year}.`;
  }

  prevEventClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.eventIntervalId);
    const i = this.dailyEvents.findIndex(e => e.id === this.randomEvent?.id);
    const idx = i <= 0 ? this.dailyEvents.length - 1 : i - 1;
    this.randomEvent = this.dailyEvents[idx];
    this.setEventFullDate();
    this.cdr.detectChanges();
  }

  nextEventClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.eventIntervalId);
    const i = this.dailyEvents.findIndex(e => e.id === this.randomEvent?.id);
    const idx = i >= this.dailyEvents.length - 1 ? 0 : i + 1;
    this.randomEvent = this.dailyEvents[idx];
    this.setEventFullDate();
    this.cdr.detectChanges();
  }

  get eventDateDisplay(): string {
    if (!this.randomEvent) {
      const todayMonth = UtilsService.getMonthTitleFromOrder(UtilsService.getTodayMonth())?.toLowerCase() ?? '';
      return `${UtilsService.getTodayDay()}. ${todayMonth}`;
    }
    const isHoliday = this.randomEvent.saintDate;
    const day = isHoliday ? this.randomEvent.saintDay : this.randomEvent.day;
    const monthNum = isHoliday ? this.randomEvent.saintMonth : this.randomEvent.month;
    const month = UtilsService.getMonthTitleFromOrder(String(monthNum))?.toLowerCase() ?? '';
    const year = this.randomEvent.description?.match(/^(\d{4})/)?.[1] ?? '';
    return year ? `${day}. ${month} ${year}.` : `${day}. ${month}`;
  }

  private birthdayAnimating = false;
  private eventIntervalId: ReturnType<typeof setInterval> | undefined;
  private birthdayIntervalId: ReturnType<typeof setInterval> | undefined;
  private quoteIntervalId: ReturnType<typeof setInterval> | undefined;
  private heritageIntervalId: ReturnType<typeof setInterval> | undefined;
  private heritageIndex = 0;
  public randomHeritage = heritageBoxes[0];
  private chronologyIntervalId: ReturnType<typeof setInterval> | undefined;
  private chronologyIndex = 0;
  public randomChronology = chronologyBoxes[0];
  private timelineIntervalId: ReturnType<typeof setInterval> | undefined;
  private timelineIndex = 0;
  private funfactIntervalId: ReturnType<typeof setInterval> | undefined;
  private funfactIndex = 0;
  private songIntervalId: ReturnType<typeof setInterval> | undefined;
  private songIndex = 0;
  private instagramIntervalId: ReturnType<typeof setInterval> | undefined;
  private instagramIndex = 0;
  public randomInstagram = instragramTypes[0];
  private tagIntervalId: ReturnType<typeof setInterval> | undefined;
  private tagIndex = 0;
  public randomTag: MiniBox | null = null;
  public prevTag: MiniBox | null = null;
  public nextTag: MiniBox | null = null;
  private cityIntervalId: ReturnType<typeof setInterval> | undefined;
  private cityIndex = 0;
  public randomCity: MiniBox | null = null;
  public prevCity: MiniBox | null = null;
  public nextCity: MiniBox | null = null;
  private personTitleMap = new Map<string, string>();
  private personImageMap = new Map<string, string>();
  private greatestIntervalId: ReturnType<typeof setInterval> | undefined;
  private greatestTouchStartX = 0;

  rotateBirthday() {
    if (this.cards.length < 2) return;
    this.animateBirthdayTo('right', this.nextBirthday);
  }

  private animateBirthdayTo(dir: 'left' | 'right', target: Person | null): void {
    if (!target || this.birthdayAnimating) return;
    this.birthdayAnimating = true;

    const slot = document.querySelector('.dc-1 .dash-card');
    const sideEl = slot?.querySelector<HTMLElement>(dir === 'right' ? '.side-next' : '.side-prev');
    const circleEl = slot?.querySelector<HTMLElement>('.birthday-circle');

    if (!sideEl || !circleEl || !target.imagePath) {
      this.randomBirthday = target;
      this.birthdayAnimating = false;
      this.cdr.detectChanges();
      return;
    }

    const sideRect = sideEl.getBoundingClientRect();
    const circleRect = circleEl.getBoundingClientRect();

    // Build flying clone starting at the side image position
    const clone = document.createElement('div');
    const img = document.createElement('img');
    img.src = target.imagePath;
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'cover';
    img.style.display = 'block';
    clone.appendChild(img);

    clone.style.position = 'fixed';
    clone.style.overflow = 'hidden';
    clone.style.left = `${sideRect.left}px`;
    clone.style.top = `${sideRect.top}px`;
    clone.style.width = `${sideRect.width}px`;
    clone.style.height = `${sideRect.height}px`;
    clone.style.borderRadius = '0.5rem';
    clone.style.filter = 'grayscale(1) brightness(0.55)';
    clone.style.zIndex = '9999';
    clone.style.pointerEvents = 'none';
    document.body.appendChild(clone);

    // Hide real circle while clone flies
    circleEl.style.opacity = '0';

    // Force reflow so the start state is painted before the transition begins
    clone.getBoundingClientRect();

    const dur = 400;
    clone.style.transition = `all ${dur}ms cubic-bezier(0.3, 0.8, 0.3, 1)`;
    clone.style.left = `${circleRect.left}px`;
    clone.style.top = `${circleRect.top}px`;
    clone.style.width = `${circleRect.width}px`;
    clone.style.height = `${circleRect.height}px`;
    clone.style.borderRadius = '1rem';
    clone.style.filter = 'none';

    setTimeout(() => {
      clone.remove();
      circleEl.style.opacity = '';
      this.randomBirthday = target;
      this.birthdayAnimating = false;
      this.cdr.detectChanges();
    }, dur + 20);
  }

  get prevBirthday(): Person | null {
    if (this.cards.length < 2) return null;
    const i = this.cards.findIndex(c => c.id === this.randomBirthday?.id);
    return this.cards[i <= 0 ? this.cards.length - 1 : i - 1];
  }

  get birthdayDateDisplay(): string {
    const p = this.randomBirthday;
    if (!p) return '';

    if (p.birthDeath?.includes('—')) {
      const deathPart = p.birthDeath.split('—')[1]?.trim() ?? '';
      const deathYear = deathPart.match(/\d{4}/)?.[0] ?? '';
      return deathYear ? `${p.birthYear} - ${deathYear}` : String(p.birthYear ?? '');
    }

    if (p.birthDay && p.birthMonth && p.birthYear) {
      const month = UtilsService.getMonthTitleFromOrder(String(p.birthMonth))?.toLowerCase() ?? '';
      return `${p.birthDay}. ${month} ${p.birthYear}.`;
    }

    return p.birthYear ? String(p.birthYear) : '';
  }

  get nextBirthday(): Person | null {
    if (this.cards.length < 2) return null;
    const i = this.cards.findIndex(c => c.id === this.randomBirthday?.id);
    return this.cards[i >= this.cards.length - 1 ? 0 : i + 1];
  }

  prevBirthdayClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.birthdayIntervalId);
    this.animateBirthdayTo('left', this.prevBirthday);
  }

  nextBirthdayClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.birthdayIntervalId);
    this.animateBirthdayTo('right', this.nextBirthday);
  }

  get prevQuote(): Quote | null {
    if (this.dailyQuotes.length < 2) return null;
    const i = this.dailyQuotes.findIndex(q => q === this.randomQuote);
    return this.dailyQuotes[i <= 0 ? this.dailyQuotes.length - 1 : i - 1];
  }

  get nextQuote(): Quote | null {
    if (this.dailyQuotes.length < 2) return null;
    const i = this.dailyQuotes.findIndex(q => q === this.randomQuote);
    return this.dailyQuotes[i >= this.dailyQuotes.length - 1 ? 0 : i + 1];
  }

  prevQuoteClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.quoteIntervalId);
    if (this.prevQuote) {
      this.randomQuote = this.prevQuote;
      this.cdr.detectChanges();
    }
  }

  nextQuoteClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.quoteIntervalId);
    if (this.nextQuote) {
      this.randomQuote = this.nextQuote;
      this.cdr.detectChanges();
    }
  }

  rotateHeritage(): void {
    this.heritageIndex = (this.heritageIndex + 1) % heritageBoxes.length;
    this.randomHeritage = heritageBoxes[this.heritageIndex];
    this.cdr.detectChanges();
  }

  prevHeritageClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.heritageIntervalId);
    this.heritageIndex = (this.heritageIndex - 1 + heritageBoxes.length) % heritageBoxes.length;
    this.randomHeritage = heritageBoxes[this.heritageIndex];
    this.cdr.detectChanges();
  }

  nextHeritageClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.heritageIntervalId);
    this.heritageIndex = (this.heritageIndex + 1) % heritageBoxes.length;
    this.randomHeritage = heritageBoxes[this.heritageIndex];
    this.cdr.detectChanges();
  }

  get prevHeritage() {
    const len = heritageBoxes.length;
    return heritageBoxes[(this.heritageIndex - 1 + len) % len];
  }

  get nextHeritage() {
    return heritageBoxes[(this.heritageIndex + 1) % heritageBoxes.length];
  }

  rotateInstagram(): void {
    this.instagramIndex = (this.instagramIndex + 1) % instragramTypes.length;
    this.randomInstagram = instragramTypes[this.instagramIndex];
    this.cdr.detectChanges();
  }

  prevInstagramClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.instagramIntervalId);
    this.instagramIndex = (this.instagramIndex - 1 + instragramTypes.length) % instragramTypes.length;
    this.randomInstagram = instragramTypes[this.instagramIndex];
    this.cdr.detectChanges();
  }

  nextInstagramClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.instagramIntervalId);
    this.instagramIndex = (this.instagramIndex + 1) % instragramTypes.length;
    this.randomInstagram = instragramTypes[this.instagramIndex];
    this.cdr.detectChanges();
  }

  private updateTagSlots(): void {
    const len = this.tagBoxes.length;
    if (!len) return;
    this.randomTag = this.tagBoxes[this.tagIndex];
    this.prevTag = len > 1 ? this.tagBoxes[(this.tagIndex - 1 + len) % len] : null;
    this.nextTag = len > 1 ? this.tagBoxes[(this.tagIndex + 1) % len] : null;
  }

  rotateTag(): void {
    if (!this.tagBoxes.length) return;
    this.tagIndex = (this.tagIndex + 1) % this.tagBoxes.length;
    this.updateTagSlots();
    this.cdr.detectChanges();
  }

  prevTagClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.tagIntervalId);
    if (!this.tagBoxes.length) return;
    this.tagIndex = (this.tagIndex - 1 + this.tagBoxes.length) % this.tagBoxes.length;
    this.updateTagSlots();
    this.cdr.detectChanges();
  }

  nextTagClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.tagIntervalId);
    if (!this.tagBoxes.length) return;
    this.tagIndex = (this.tagIndex + 1) % this.tagBoxes.length;
    this.updateTagSlots();
    this.cdr.detectChanges();
  }

  private updateCitySlots(): void {
    const len = this.cityTagBoxes.length;
    if (!len) return;
    this.randomCity = this.cityTagBoxes[this.cityIndex];
    this.prevCity = len > 1 ? this.cityTagBoxes[(this.cityIndex - 1 + len) % len] : null;
    this.nextCity = len > 1 ? this.cityTagBoxes[(this.cityIndex + 1) % len] : null;
  }

  rotateCity(): void {
    if (!this.cityTagBoxes.length) return;
    this.cityIndex = (this.cityIndex + 1) % this.cityTagBoxes.length;
    this.updateCitySlots();
    this.cdr.detectChanges();
  }

  prevCityClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.cityIntervalId);
    if (!this.cityTagBoxes.length) return;
    this.cityIndex = (this.cityIndex - 1 + this.cityTagBoxes.length) % this.cityTagBoxes.length;
    this.updateCitySlots();
    this.cdr.detectChanges();
  }

  nextCityClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.cityIntervalId);
    if (!this.cityTagBoxes.length) return;
    this.cityIndex = (this.cityIndex + 1) % this.cityTagBoxes.length;
    this.updateCitySlots();
    this.cdr.detectChanges();
  }

  rotateChronology(): void {
    this.chronologyIndex = (this.chronologyIndex + 1) % chronologyBoxes.length;
    this.randomChronology = chronologyBoxes[this.chronologyIndex];
    this.cdr.detectChanges();
  }

  prevChronologyClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.chronologyIntervalId);
    this.chronologyIndex = (this.chronologyIndex - 1 + chronologyBoxes.length) % chronologyBoxes.length;
    this.randomChronology = chronologyBoxes[this.chronologyIndex];
    this.cdr.detectChanges();
  }

  nextChronologyClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.chronologyIntervalId);
    this.chronologyIndex = (this.chronologyIndex + 1) % chronologyBoxes.length;
    this.randomChronology = chronologyBoxes[this.chronologyIndex];
    this.cdr.detectChanges();
  }

  get prevChronology() {
    const len = chronologyBoxes.length;
    return chronologyBoxes[(this.chronologyIndex - 1 + len) % len];
  }

  get nextChronology() {
    return chronologyBoxes[(this.chronologyIndex + 1) % chronologyBoxes.length];
  }

  rotateTimeline(): void {
    if (!this.timelineEvents.length) return;
    this.timelineIndex = (this.timelineIndex + 1) % this.timelineEvents.length;
    this.randomTimelineEvent = this.timelineEvents[this.timelineIndex];
    this.cdr.detectChanges();
  }

  prevTimelineClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.timelineIntervalId);
    this.timelineIndex = (this.timelineIndex - 1 + this.timelineEvents.length) % this.timelineEvents.length;
    this.randomTimelineEvent = this.timelineEvents[this.timelineIndex];
    this.cdr.detectChanges();
  }

  nextTimelineClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.timelineIntervalId);
    this.timelineIndex = (this.timelineIndex + 1) % this.timelineEvents.length;
    this.randomTimelineEvent = this.timelineEvents[this.timelineIndex];
    this.cdr.detectChanges();
  }

  async buildPersonTitleMap(): Promise<void> {
    const persons = await this.personsService.getItems();
    for (const p of persons) {
      if (p.slug) {
        this.personTitleMap.set(p.slug, p.fullTitle ?? '');
        this.personImageMap.set(p.slug, p.imagePath ?? '');
      }
    }
  }

  get randomQuoteAuthorImage(): string {
    return this.personImageMap.get(this.randomQuote?.authorSlug ?? '') ?? '';
  }

  get quoteAuthorTitle(): string {
    return this.personTitleMap.get(this.randomQuote?.authorSlug ?? '') ?? '';
  }

  get quoteTextSize(): string {
    const len = this.randomQuote?.text?.length ?? 0;
    if (len < 80) return '2rem';
    if (len < 160) return '1.6rem';
    if (len < 250) return '1.3rem';
    return '1.1rem';
  }

  async getCategories() {
    this.categoryList = await this.categoriesService.getItemsSorted('order');
  }


  async getTimelineEvents(): Promise<void> {
    const chronologies = await this.chronologyService.getItemsSorted('age');
    this.timelineEvents = chronologies.filter(e => e.typeId === ChronologyType.state);
    if (this.timelineEvents.length) {
      this.timelineIndex = Math.floor(Math.random() * this.timelineEvents.length);
      this.randomTimelineEvent = this.timelineEvents[this.timelineIndex];
      this.cdr.detectChanges();
    }
  }


  async getPersonCards() {
    const cards = await this.getBirthdayPersons() as Person[];
    this.cards = cards;
    if (cards.length) {
      this.randomBirthday = cards[0];
      this.cdr.detectChanges();
    }
    this.rotateBirthday();
  }

  async getTiles(): Promise<void> {
    const result = await this.personsService.getItemsQuery('frontPage', '==', true);

    result.sort((a, b) => (a.birthYear as number) - (b.birthYear as number));
    this.tileList = result;
  }


  async getDailyHolidays(): Promise<void> {
    const day = UtilsService.getTodayDay();
    const month = UtilsService.getTodayMonth();

    const holidays = await this.saintsService.getItemsSorted('saintDate');
    const fixHolidays = UtilsService.setMovableHolidays(holidays);

    const filteredItems = fixHolidays
      .map(h => {
        (h as any).isHoliday = true;
        return h;
      })
      .filter(h =>
        String(h.saintMonth) === String(month) &&
        String(h.saintDay) === String(day)
      );

    this.dailyEvents = UtilsService.removeDuplicatesByKey(
      [...filteredItems, ...this.dailyEvents],
      'slug');

    this.randomizeEvent();
    this.cdr.detectChanges();
  }

  async getFunFacts() {
    const conditions: any = [['day', 0], ['month', 0]];
    const response = await this.historicalEventsService.getItemsQueries(conditions);

    this.shuffle.transform(response);
    this.funfactBoxes = response.map((item: HistoricalEvent) => {
      return {
        style: '',
        id: item.id || Math.floor(Math.random() * 100_000),
        imagePath: item.imagePath || '',
        title: item.title || '',
        description: item.description || '',
        link: item.slug || '',
        icon: null,
        background: '',
      };
    });
    if (this.funfactBoxes.length) {
      this.randomFunfact = this.funfactBoxes[0];
    }
  }

  rotateFunfact(): void {
    if (!this.funfactBoxes.length) return;
    this.funfactIndex = (this.funfactIndex + 1) % this.funfactBoxes.length;
    this.randomFunfact = this.funfactBoxes[this.funfactIndex];
    this.cdr.detectChanges();
  }

  prevFunfactClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.funfactIntervalId);
    if (!this.funfactBoxes.length) return;
    this.funfactIndex = (this.funfactIndex - 1 + this.funfactBoxes.length) % this.funfactBoxes.length;
    this.randomFunfact = this.funfactBoxes[this.funfactIndex];
    this.cdr.detectChanges();
  }

  nextFunfactClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.funfactIntervalId);
    if (!this.funfactBoxes.length) return;
    this.funfactIndex = (this.funfactIndex + 1) % this.funfactBoxes.length;
    this.randomFunfact = this.funfactBoxes[this.funfactIndex];
    this.cdr.detectChanges();
  }

  rotateSong(): void {
    if (!this.songList.length) return;
    this.songIndex = (this.songIndex + 1) % this.songList.length;
    this.randomSong = this.songList[this.songIndex];
    this.cdr.detectChanges();
  }

  prevSongClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.songIntervalId);
    if (!this.songList.length) return;
    this.songIndex = (this.songIndex - 1 + this.songList.length) % this.songList.length;
    this.randomSong = this.songList[this.songIndex];
    this.cdr.detectChanges();
  }

  nextSongClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.songIntervalId);
    if (!this.songList.length) return;
    this.songIndex = (this.songIndex + 1) % this.songList.length;
    this.randomSong = this.songList[this.songIndex];
    this.cdr.detectChanges();
  }

  get centerGreatest(): Person | null {
    return this.tileList[this.greatestIndex] ?? null;
  }

  get prevGreatest(): Person | null {
    if (this.tileList.length < 2) return null;
    return this.tileList[(this.greatestIndex - 1 + this.tileList.length) % this.tileList.length];
  }

  get nextGreatest(): Person | null {
    if (this.tileList.length < 2) return null;
    return this.tileList[(this.greatestIndex + 1) % this.tileList.length];
  }

  get prevGreatestList(): Person[] {
    const n = this.tileList.length;
    if (n < 2) return [];
    const count = Math.min(this.sideCardCount(), n - 1);
    return Array.from({ length: count }, (_, i) =>
      this.tileList[(this.greatestIndex - i - 1 + n) % n]
    );
  }

  get nextGreatestList(): Person[] {
    const n = this.tileList.length;
    if (n < 2) return [];
    const count = Math.min(this.sideCardCount(), n - 1);
    return Array.from({ length: count }, (_, i) =>
      this.tileList[(this.greatestIndex + i + 1) % n]
    );
  }

  getDeathYear(person: Person): string {
    if (!person.birthDeath?.includes('—')) return '';
    return person.birthDeath.split('—')[1]?.match(/\d{4}/)?.[0] ?? '';
  }

  getBirthDate(person: Person): string {
    if (!person.birthYear) return '';
    if (person.birthDay && person.birthMonth) {
      return `${person.birthDay}.${person.birthMonth}.${person.birthYear}`;
    }
    return String(person.birthYear);
  }

  rotateGreatest(): void {
    if (!this.tileList.length) return;
    this.greatestIndex = (this.greatestIndex + 1) % this.tileList.length;
    this.cdr.detectChanges();
  }

  prevGreatestClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.greatestIntervalId);
    this.greatestIndex = (this.greatestIndex - 1 + this.tileList.length) % this.tileList.length;
    this.cdr.detectChanges();
  }

  nextGreatestClick(event: Event): void {
    event.stopPropagation();
    clearInterval(this.greatestIntervalId);
    this.greatestIndex = (this.greatestIndex + 1) % this.tileList.length;
    this.cdr.detectChanges();
  }

  randomizeGreatest(): void {
    if (this.tileList.length < 2) return;
    clearInterval(this.greatestIntervalId);
    let idx: number;
    do {
      idx = Math.floor(Math.random() * this.tileList.length);
    } while (idx === this.greatestIndex);
    this.greatestIndex = idx;
    this.cdr.detectChanges();
  }

  onGreatestTouchStart(event: TouchEvent): void {
    this.greatestTouchStartX = event.touches[0].clientX;
  }

  onGreatestTouchEnd(event: TouchEvent): void {
    const dx = event.changedTouches[0].clientX - this.greatestTouchStartX;
    if (Math.abs(dx) < 50) return;
    clearInterval(this.greatestIntervalId);
    this.greatestIndex = dx < 0
      ? (this.greatestIndex + 1) % this.tileList.length
      : (this.greatestIndex - 1 + this.tileList.length) % this.tileList.length;
    this.cdr.detectChanges();
  }

  async getDailyEvents() {
    let day = UtilsService.getTodayDay();
    let month = UtilsService.getTodayMonth();

    const conditions: any[] = [['day', +day], ['month', +month]];

    const response = await this.historicalEventsService.getItemsQueries(conditions)

    response.sort((a: HistoricalEvent, b: HistoricalEvent) => {
      const bYear = a.description?.substring(0, 4);
      const aYear = b.description?.substring(0, 4);
      return bYear!.localeCompare(aYear!);
    });
    this.dailyEvents = UtilsService.removeDuplicatesByKey([...response, ...this.dailyEvents], 'slug');
    this.randomizeEvent();
    this.cdr.detectChanges();

  }

  checkDailyQuiz() {
    const today = UtilsService.getTodayFormatted();
    const storageName = `quiz-${today}`;
    const storageQuiz = localStorage.getItem(storageName);

    if (storageQuiz) {
      this.gamePoints = JSON.parse(storageQuiz);

      this.totalScore = this.gamePoints.reduce((a, b) => a + b);
    }
  }

  async getBirthdayPersons() {
    const birthdayDay = UtilsService.getTodayDay();
    const monthOrder = UtilsService.getTodayMonth();
    const monthTitle = UtilsService.getMonthTitleFromOrder(monthOrder).toLowerCase();
    this.currentDate = `${birthdayDay}. ${monthTitle}`;

    const conditions: [string, any][] = [['birthDay', +birthdayDay], ['birthMonth', +monthOrder]];

    const response = await this.personsService.getItemsQueries(conditions);
    response.sort((a, b) =>
      this.getBirthdayRank(a) - this.getBirthdayRank(b) ||
      Number(a.birthYear) - Number(b.birthYear)
    );
    return response;
  }

  // Starred persons first (frontPage > topTen > localHero), then the living, then the deceased
  private getBirthdayRank(person: Person): number {
    if (person.frontPage) return 0;
    if (person.topTen) return 1;
    if (person.localHero) return 2;
    return this.isDeceased(person) ? 4 : 3;
  }

  private isDeceased(person: Person): boolean {
    return !!person.birthDeath?.split(' - ')[1]?.trim();
  }

  onDailySaint() {
    this.router.navigateByUrl('srpsko-nasledje/slave-i-praznici');
  }

  onCategories = () => {
    this.router.navigateByUrl('/kategorije');
  };

  onCategory(path: string) {
    this.router.navigateByUrl(`/kategorija/${path}`);
  };

  onQuotesPage = () => {
    this.router.navigateByUrl('/citati');
  };

  onSongsPage = () => {
    this.router.navigateByUrl('/poezija');
  };

  onQuiz = () => {
    this.router.navigateByUrl(`kviz`);
  };

  onInstragram = () => {
    this.router.navigateByUrl(`instagram`);
  };

  onSurnames = () => {
    this.router.navigateByUrl(`prezime`);
  };

  onFirstnames = () => {
    this.router.navigateByUrl(`ime`);
  };

  onBirthdayHeadline = () => {
    const path = UtilsService.getTodaySlug();
    this.router.navigateByUrl(`kalendar/${path}/rodjeni`);
  };

  onDailyEvent = () => {
    const path = UtilsService.getTodaySlug();
    this.router.navigateByUrl(`kalendar/${path}/dogadjaji`);
  }

  onHeritage = () => {
    this.router.navigateByUrl(`srpsko-nasledje`);
  };

  onHeritageBox(path: string) {
    this.router.navigateByUrl(`srpsko-nasledje/${path}`);
  }

  onChronology = () => {
    this.router.navigateByUrl(`vremeplov`);
  };

  onChronologyBox(path: string) {
    this.router.navigateByUrl(`vremeplov/${path}`);
  }

  onFunFacts = () => {
    this.router.navigateByUrl(`zanimljivosti`);
  };

  goToQuotes() {
    this.router.navigateByUrl(`citati`);
  }

  goToPerson(slug: string) {
    this.router.navigateByUrl(`biografija/${slug}`);
  }

  goToCalendar() {
    const path = UtilsService.getTodaySlug();
    this.router.navigateByUrl(`kalendar/${path}/rodjeni`);
  }

  goToEvents() {
    const path = UtilsService.getTodaySlug();
    this.router.navigateByUrl(`kalendar/${path}/dogadjaji`);
  }

  onFunFactBox(item: MiniBox) {
    const items = [...this.funfactBoxes];
    const data = { item, items };
    const sheet = BasicSheetComponent<any>;

    const panelClass = 'basic-sheet-overlay';
    const scrollStrategy = new NoopScrollStrategy();
    const disableClose = false;

    const href = this.router.url;
    this.location.go(href + '#');

    const sheetRef = this.matBottomSheet.open(sheet, {
      data, panelClass, scrollStrategy, disableClose
    });

    sheetRef.afterDismissed().subscribe((onX: boolean) => {
      if (onX) {
        this.location.back();
      } else {
        if (window.location.href.endsWith('#')) {
          this.location.back();
        };
      }
    });
  }

  onTag = () => {
    this.router.navigateByUrl(`tagovi`);
  };

  onTagBox = (path: string) => {
    const link = `tag/${path}/licnosti`;
    this.router.navigateByUrl(link);
  };

  onInstagramBox = (path: string) => {
    const link = `instagram#${path}`;
    this.router.navigateByUrl(link);
  };

  onSurname = (title: string) => {
    const link = `prezime/${title}`;
    this.router.navigateByUrl(link);
  };

  onFirstname = (title: string) => {
    const link = `ime/${title}`;
    this.router.navigateByUrl(link);
  };

  onTimeline = () => {
    this.router.navigateByUrl(`vremeplov/razvoj-srpske-drzave`);
  };

  styleFirstWord(randomEvent: any) {
    const text = randomEvent.description;
    const firstWordEnd = text.indexOf(' ');
    if (randomEvent.isHoliday) {
      return text;
    }

    return `<span class="first-word">${text.substring(0, firstWordEnd)}</span>${text.substring(firstWordEnd)}`;
  }


  extractDate(birthDeath: string): string {
    return birthDeath.split('.')[0] + '.';
  }

  extractMonth(birthDeath: string): string {
    return birthDeath.split('.')[1] + '.';
  }

  extractRest(birthDeath: string): string {
    const parts = birthDeath.split('.');
    parts.shift();
    parts.shift();
    return parts.join('.');
  }

  onAuthor(slug: string | null) {
    if (slug) {
      this.router.navigateByUrl(`biografija/${slug}`);
    }
  }

  onSong(song: Song) {
    this.router.navigateByUrl(`stihovi/${song.slug!}`);
  }
}

